<template>
  <div class="px-2">
    <form :class="{'opacity-50': isWorking}" @submit.prevent="processUpdate" @input="setHasUnsavedChanges">
      <pdl-section-header size="xl" :is-dividing="true" class="mt-3">
        <template #content>
          <pdl-heading :level="1">
            {{ $t('rcp.profile.phone.title') }}
          </pdl-heading>
        </template>
      </pdl-section-header>

      <p class="mb-3 text-base">
        {{ $t('rcp.profile.phone.enterNew') }}
      </p>

      <pdl-callout v-if="message" :kind="messageType">
        <template #content>
          {{ message }}
        </template>
      </pdl-callout>

      <div class="max-w-sm mb-6">
        <pdl-form-group
          v-mask="phoneFormat"
          :model-value="phone"
          name="phone"
          :data-mask="phoneFormat"
          :help-text="phoneHelpText"
          required
          :maxlength="phoneMaxLength"
          :label="$t('text.address.phone')"
          :error="getErrorMessage('phone')"
          qaid="phone-number-field"
          allowed-characters-regex="[\d-+. ()]"
          @update:modelValue="handleInput"
          @blur="v$.$touch"
        />

        <div class="buttons">
          <trek-button primary type="submit" class="w-2/3" :disabled="isWorking">
            <span>{{ $t('text.account.profile.saveUpdates') }}</span>
          </trek-button>
          <trek-button secondary @click="cancel">
            <span>{{ $t('text.account.profile.cancel') }}</span>
          </trek-button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import {mask} from '@/directives/masks';
import TrekButton from '@/components/TrekButton';
import {PdlSectionHeader, PdlHeading} from '@pedal/pdl-section-header';
import {UserAccountBase} from '@/components/containers/my-account/utils/user-account-base.js';
import {PdlFormGroup} from '@pedal/pdl-form-group';
import {TrekValidationMixin} from '@/utils/validation/trek-validation-mixin.js';
import {mapActions, mapState} from 'vuex';
import {getPhoneMaskPattern} from '@/utils/masks/phone-mask-patterns.js';
import {PdlCallout} from '@pedal/pdl-callout';
import UserOccApi from '@/api/user-occ.js';
import isEmpty from 'lodash/isEmpty';

export default {
  name: 'UpdatePhoneNumberContainer',
  components: {
    TrekButton,
    PdlSectionHeader,
    PdlHeading,
    PdlFormGroup,
    PdlCallout,
  },
  directives: {mask},
  extends: UserAccountBase,
  mixins: [TrekValidationMixin],
  props: {
    isRideClubAccount: {
      type: Boolean,
      default: () => false,
    },
  },
  setup() {
    return {
      ...UserAccountBase.setup(),
    };
  },
  data() {
    return {
      phone: '',
      isWorking: false,
      message: '',
      messageType: 'info',
    };
  },
  validations() {
    return {
      phone: {
        required: this.trekValidators.requiredWithCustomMessage('consumerReturns.B2B.phoneNumber.required'),
        maxLength: this.trekValidators.maxLengthWithCustomMessage('address.phone.invalid', this.phoneMaxLength),
        minLength: this.trekValidators.minLengthWithCustomMessage('address.phone.invalid', this.minLengthofLocale),
      },
    };
  },

  computed: {
    ...mapState('user', ['userProfile']),
    ...mapState('backend', ['locale']),
    phoneFormat() {
      if (this.locale === 'usb2b') {
        return getPhoneMaskPattern('us');
      }
      return getPhoneMaskPattern(this.locale);
    },
    fieldName() {
      return 'phone';
    },
    minLengthofLocale() {
      if (this.locale === 'jp') return this.phoneMaxLength - 1;
      else if (['de', 'at'].includes(this.locale)) return 1;
      else return this.phoneMaxLength;
    },
    phoneMaxLength() {
      return this.phoneFormat?.length;
    },
    phoneHelpText() {
      return this.isRideClubAccount ? this.$t('rcp.profile.phone.message') : this.$t('text.phoneRequired');
    },
  },
  watch: {
    userProfile() {
      if (!isEmpty(this.userProfile)) {
        this.phone = this.userProfile?.cdcOnlyData?.phone;
      }
    },
  },

  async mounted() {
    await this.fetchUserProfile();
  },

  methods: {
    ...mapActions('user', ['fetchUserProfile']),
    async processUpdate() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.isWorking = true;
      try {
        const response = await UserOccApi.patchUserProfile({cdcOnlyData: {phone: this.phone}});
        await this.fetchUserProfile(response);
        this.setHasUnsavedChanges(false);
        this.$notify({
          type: 'success',
          message: this.$t('text.account.profile.confirmationUpdated'),
        });
      } catch (e) {
        this.$notify({
          type: 'error',
          message: this.$t('errorMessage.unableToProcess'),
        });
      } finally {
        this.isWorking = false;
      }
    },
    resetForm() {
      this.phone = '';
      this.v$.$reset();
    },
    handleInput(value) {
      this.phone = value;
      this.v$.phone.$reset();
    },
  },
};
</script>
